import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { PreloadAllModules, Router, RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { HomeComponent } from './home/home.component';
import { SocialLoginModule, SocialAuthServiceConfig, GoogleLoginProvider, GoogleInitOptions } from '@abacritt/angularx-social-login';
import { SigninComponent } from './signin/signin.component';
import { ManageSitesComponent } from './manage-sites/manage-sites.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { ReplacePipe } from './pipes/replace.pipe';
import { GenericModalComponent } from './generic-modal/generic-modal.component';
import { IndexingHistoryComponent } from './indexing-history/indexing-history.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { BlogComponent } from './blog/blog.component';
import { FaqComponent } from './faq/faq.component';
import { PricingComponent } from './pricing/pricing.component';
import { CustomHttpInterceptor } from './CustomHttpInterceptor';
import { BlogDetailsComponent } from './blog-details/blog-details.component';
import { GapiService } from './services/gapi.service';
import { AlertComponent } from './alert/alert.component';
import { SearchSitePipe } from './pipes/search-site.pipe';
import { AccountSettingsComponent } from './account-settings/account-settings.component';
import { PaginationComponent } from './pagination/pagination.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { ServiceAccountComponent } from './service-account/service-account.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { TermsComponent } from './terms/terms.component';
import { PaymentResultComponent } from './payment-result/payment-result.component';
import { SeoToolsComponent } from './seo-tools/seo-tools.component';
import { WebsiteTitleCheckerComponent } from './seo-tools/website-title-checker/website-title-checker.component';
import { MetaDescriptionCheckerComponent } from './seo-tools/meta-description-checker/meta-description-checker.component';
import { OpenGraphMetaCheckerComponent } from './seo-tools/open-graph-meta-checker/open-graph-meta-checker.component';
import * as Sentry from "@sentry/angular-ivy";
import { AutoIndexQueueComponent } from './auto-index-queue/auto-index-queue.component';

const googleLoginOptions: GoogleInitOptions = {
  scopes: ['https://www.googleapis.com/auth/webmasters.readonly', 'https://www.googleapis.com/auth/userinfo.profile', 'https://www.googleapis.com/auth/userinfo.email'],
  oneTapEnabled: false,
  prompt: "",
}

@NgModule({
  declarations: [
    AppComponent,
    NavMenuComponent,
    HomeComponent,
    SigninComponent,
    ManageSitesComponent,
    HeaderComponent,
    FooterComponent,
    ReplacePipe,
    SearchSitePipe,
    GenericModalComponent,
    IndexingHistoryComponent,
    PageNotFoundComponent,
    FaqComponent,
    BlogComponent,
    PricingComponent,
    BlogDetailsComponent,
    AlertComponent,
    AccountSettingsComponent,
    PaginationComponent,
    SpinnerComponent,
    ServiceAccountComponent,
    PrivacyPolicyComponent,
    TermsComponent,
    PaymentResultComponent,
    SeoToolsComponent,
    WebsiteTitleCheckerComponent,
    MetaDescriptionCheckerComponent,
    OpenGraphMetaCheckerComponent,
    AutoIndexQueueComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    FormsModule,
    SocialLoginModule,
    RouterModule.forRoot([
      { path: '', component: HomeComponent, pathMatch: 'full', data: { preload: true } },
      { path: 'signin/:issue', component: SigninComponent },
      { path: 'signin', component: SigninComponent },
      { path: 'manage-sites', component: ManageSitesComponent },
      { path: 'indexing-history', component: IndexingHistoryComponent },
      { path: 'auto-index-queue', component: AutoIndexQueueComponent },
      { path: 'blog', component: BlogComponent, title:"Blog | RankAhead" },
      { path: 'blog/:slug', component: BlogDetailsComponent },
      { path: 'faq', component: FaqComponent },
      { path: 'pricing', component: PricingComponent },
      { path: 'account-settings', component: AccountSettingsComponent },
      { path: 'service-accounts', component: ServiceAccountComponent },
      { path: 'privacy-policy', component: PrivacyPolicyComponent },
      { path: 'terms', component: TermsComponent },
      { path: 'seo-tools', component: SeoToolsComponent },
      { path: 'website-title-checker', component: WebsiteTitleCheckerComponent },
      { path: 'meta-description-checker', component: MetaDescriptionCheckerComponent },
      { path: 'open-graph-checker', component: OpenGraphMetaCheckerComponent },
      { path: 'subscription-result/:type', component: PaymentResultComponent },
      //Wild Card Route for 404 request
      {
        path: '**', pathMatch: 'full',
        component: PageNotFoundComponent
      },
    ], { scrollPositionRestoration: 'enabled', preloadingStrategy: PreloadAllModules })
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: (gapiService: GapiService) => () => gapiService.loadGapi(), deps: [GapiService], multi: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        true: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              '226015714415-lg7hkqti2helefkhmq68v3a20uhqv7kf.apps.googleusercontent.com', googleLoginOptions
            )
          }
          

        ],
        onError: (err) => {
          console.error(err);
        }
      } as SocialAuthServiceConfig,
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
